// src/services/server.js
// Central place for API base URL + backend calls
//
// Design goals:
// - Always send cookies by default (credentials: "include")
// - Always attach X-CSRFToken for unsafe methods (POST/PUT/PATCH/DELETE) when csrftoken cookie exists
// - Never “lose” headers due to overwrites
// - Use one single request() helper for JSON + FormData (no raw fetch() in endpoints)
// - Allow explicit opt-out per call: { csrf: false } (rare)
//export const API_BASE = "http://localhost:8000";
//export const API_BASE = "/api"; // legacy same-origin path
export const API_BASE = "https://api.qada.io";
export function api(path) {
  if (!API_BASE) return path;
  if (path.startsWith("http")) return path;
  return `${API_BASE}${path}`;
}

export async function uploadWorkspaceDocument({
  workspaceKey,     // organisationnummer
  file,
  ddqQid,           // e.g. "10.3"
  folderPath = [],  // ["03_Contracts", "Customers"]
}) {
  const form = new FormData();
  form.append("file", file);
  form.append("ddq_qid", ddqQid);
  form.append("folder_path", JSON.stringify(folderPath));

  return request(
    `/users/workspaces/${workspaceKey}/dataroom/upload/`,
    {
      method: "POST",
      body: form,
    }
  );
}


/* ============================== Helpers ============================== */

// CSRF token stored in memory (fetched from API response, not cookies)
// This works across subdomains and Safari's ITP restrictions
let _csrfToken = null;

export function setCsrfToken(token) {
  _csrfToken = token;
}

export function getCsrfToken() {
  // Try memory first, fall back to cookie
  if (_csrfToken) return _csrfToken;
  const m = document.cookie.match(new RegExp(`(^|;\\s*)csrftoken=([^;]*)`));
  return m ? decodeURIComponent(m[2]) : null;
}


function isUnsafe(method) {
  const m = String(method || "GET").toUpperCase();
  return !["GET", "HEAD", "OPTIONS", "TRACE"].includes(m);
}

async function parseResponse(res) {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { raw: text };
  }
}

// Global 403 handler — set by ApiErrorProvider
let _on403 = null;
export function setOn403Handler(fn) { _on403 = fn; }

async function throwHttpError(res, url) {
  const body = await parseResponse(res);
  const err = new Error(`HTTP ${res.status} ${res.statusText}`);
  err.status = res.status;
  err.url = url;
  err.body = body;

  // Trigger global 403 handler ONLY for workspace-scoped endpoints
  // (dataroom, dd, koncern, logg, settings — pages that require workspace membership)
  if (res.status === 403 && _on403 && /\/workspaces\/\w+\/(dataroom|ddq|koncern|nda|users|leave|pipeline)/.test(url)) {
    _on403(err);
  }

  throw err;
}

/**
 * Low-level fetch wrapper that:
 * - defaults credentials to include
 * - always has a headers object
 * - attaches X-CSRFToken for unsafe methods unless csrf:false
 */
async function fetchWithCsrf(path, options = {}) {
  const {
    method = "GET",
    headers = {},
    credentials = "include",
    csrf = true,
    ...rest
  } = options;

  const finalHeaders = { ...headers };

  if (csrf && isUnsafe(method)) {
    const token = getCsrfToken();
    if (token) finalHeaders["X-CSRFToken"] = token;
  }

  return fetch(api(path), {
    method,
    credentials,
    headers: finalHeaders,
    ...rest,
  });
}

/* ============================== Core request ============================== */
/**
 * request() supports:
 * - JSON and FormData
 * - consistent CSRF handling
 * - consistent error handling + JSON parsing
 */
export async function request(path, params = {}) {
  const {
    method = "GET",
    body,
    headers = {},
    signal,
    formData = false,
    credentials = "include",
    csrf = true,
    ...rest
  } = params;

  const opts = {
    method,
    signal,
    credentials,
    csrf,
    ...rest,
  };

  const h = { ...headers };

  if (body != null) {
    if (formData && body instanceof FormData) {
      opts.body = body;
      // DO NOT set Content-Type for FormData
    } else {
      opts.body = typeof body === "string" ? body : JSON.stringify(body);
      if (!h["Content-Type"]) h["Content-Type"] = "application/json";
    }
  }

  opts.headers = h;

  const res = await fetchWithCsrf(path, opts);
  if (!res.ok) await throwHttpError(res, api(path));
  return parseResponse(res);
}

/* ============================== Processing (company/workspace) ============================== */

function normalizeOrgnr(orgnr) {
  const s = String(orgnr || "").trim();
  if (!s) throw new Error("Ogiltigt organisationsnummer");
  // Workspace slug (contains letters): pass through — the backend resolves
  // slug → owning Organisation. Lets URLs that use slug instead of orgnr
  // work for every endpoint without a frontend rewrite.
  if (/[a-z]/i.test(s)) return s;
  const q = s.replace(/\D/g, "");
  if (q.length !== 10) throw new Error("Ogiltigt organisationsnummer");
  return q;
}

// POST /processing/workspace/<orgnr>/ensure/
export function ensureWorkspace(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/ensure/`, {
    method: "POST",
    signal,
    credentials,
  });
}

/**
 * SIE uploads are PRIVATE and must be tied to workspace (not the global company).
 * POST /processing/workspace/<orgnr>/sie/upload/  (multipart)
 *
 * Accepts:
 *  - a single File
 *  - an array of File
 *  - an array of { year: 2024, file: File } (recommended)
 */
export async function uploadWorkspaceSie(orgnr, sieFileOrFiles, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);

  const files = Array.isArray(sieFileOrFiles) ? sieFileOrFiles : [sieFileOrFiles];
  const onlyFiles = files.filter(Boolean);
  if (!onlyFiles.length) return { uploaded: 0 };

  // Ensure workspace exists (idempotent)
  await ensureWorkspace(q, { signal, credentials });

  const form = new FormData();

  onlyFiles.forEach((item, idx) => {
    // Preferred: { year, file }
    if (item && item.file instanceof File && item.year) {
      form.append(`sie_${String(item.year)}`, item.file);
      return;
    }
    // Plain File
    if (item instanceof File) {
      form.append(idx === 0 ? "sie_lopande" : `sie_lopande_${idx}`, item);
      return;
    }
    // Custom object: { key, file }
    if (item && item.file instanceof File) {
      form.append(item.key || "sie_lopande", item.file);
    }
  });

  return request(`/processing/workspace/${encodeURIComponent(q)}/sie/upload/`, {
    method: "POST",
    body: form,
    formData: true,
    signal,
    credentials,
  });
}

/**
 * Financial statements (bokslut) are "public-ish" and can be tied to global company docs.
 * POST /processing/company/<orgnr>/financials/upload/ (multipart)  -- ONLY BOKSLUT
 */
export async function uploadFinancials(orgnr, formData, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  if (!(formData instanceof FormData)) {
    throw new Error("uploadFinancials expects a FormData instance");
  }
  return request(`/processing/company/${encodeURIComponent(q)}/financials/upload/`, {
    method: "POST",
    body: formData,
    formData: true,
    signal,
    credentials,
  });
}

// GET /processing/company/validate/?q=
export function validateCompany(orgnr, { signal, credentials } = {}) {
  const qs = new URLSearchParams({ q: (orgnr || "").trim() });
  return request(`/processing/company/validate/?${qs.toString()}`, {
    signal,
    credentials,
  });
}

// GET /processing/company/<orgnr>/
export function getCompanyInfo(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/company/${encodeURIComponent(q)}/`, {
    signal,
    credentials,
  });
}

export function syncCompany(orgnr, formData, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);

  if (!(formData instanceof FormData)) {
    throw new Error("syncCompany expects a FormData instance");
  }

  return request(`/processing/company/${encodeURIComponent(q)}/sync/`, {
    method: "POST",
    body: formData,
    formData: true,          // ✅ tells request() NOT to set JSON Content-Type
    signal,
    credentials,             // defaults to "include" in request(), but ok to pass through
  });
}
// GET /processing/company/<orgnr>/bokslut/
export function listBokslut(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/company/${encodeURIComponent(q)}/bokslut/`, {
    signal,
    credentials,
  });
}

// GET /processing/company/<orgnr>/bokslut/<dokument_id>/
export function ensureBokslut(orgnr, dokumentId, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(
    `/processing/company/${encodeURIComponent(q)}/bokslut/${encodeURIComponent(dokumentId)}/`,
    { signal, credentials }
  );
}

// GET /processing/company/<orgnr>/documents/<dokument_id>/processing/
export function getDocumentProcessingStatus(orgnr, dokumentId, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(
    `/processing/company/${encodeURIComponent(q)}/documents/${encodeURIComponent(dokumentId)}/processing/`,
    { signal, credentials }
  );
}

/* ============================== Workspace UI endpoints ============================== */

export function getWorkspaceDashboard(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/dashboard/`, {
    signal,
    credentials,
  });
}

export function getWorkspaceAnnualReports(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/annual-reports/`, {
    signal,
    credentials,
  });
}

export function saveWorkspaceManualFinancials(orgnr, payload, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/financials/manual/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

/* ============================== Owner questionnaire (workspace) ============================== */

export function getOwnerQuestionnaire(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/owner-questionnaire/`, {
    signal,
    credentials,
  });
}

export function saveOwnerQuestionnaire(orgnr, payload = {}, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/owner-questionnaire/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

/* ============================== Valuation (workspace) ============================== */

export function getCompanyValuation(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/company/company/${encodeURIComponent(q)}/valuation/`, {
    signal,
    credentials,
  });
}

export function saveCompanyValuationManual(orgnr, inputs = {}, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/valuation/manual-inputs/`, {
    method: "POST",
    body: { inputs },
    signal,
    credentials,
  });
}

export function revertCompanyValuation(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/valuation/revert/`, {
    method: "POST",
    signal,
    credentials,
  });
}

export function useCompanyValuationManual(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/valuation/use-manual/`, {
    method: "POST",
    signal,
    credentials,
  });
}

/* ============================== BolagsAPI (enriched company data) ============================== */

export function getCompanyEnriched(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/retriever/company/${encodeURIComponent(q)}/enriched/`, {
    signal,
    credentials,
  });
}

export function getCompanyFinancials(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/retriever/company/${encodeURIComponent(q)}/financials/`, {
    signal,
    credentials,
  });
}

export function getCompanyFinancialHealth(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/retriever/company/${encodeURIComponent(q)}/financial-health/`, {
    signal,
    credentials,
  });
}

/* ============================== Buyer matching (FöretagsAPI) ============================== */

export function buyerSearch(body, { signal, credentials } = {}) {
  return request(`/retriever/buyer-search/`, {
    method: "POST",
    body,
    signal,
    credentials,
  });
}

export function getBuyerSearchCategories({ signal, credentials } = {}) {
  return request(`/retriever/prospect/categories/`, { signal, credentials });
}

/* ============================== Billing (Stripe) ============================== */

// Does the firm have a card on file? Drives the first-login popup.
export function getBillingStatus({ signal, credentials } = {}) {
  return request(`/payments/billing-status/`, { signal, credentials });
}

// Returns SetupIntent client_secret for Stripe Elements popup.
export function createSetupIntent({ signal, credentials } = {}) {
  return request(`/payments/onboarding/setup-intent/`, {
    method: "POST",
    body: {},
    signal,
    credentials,
  });
}

// Stripe Checkout Session (setup mode) — returns a Stripe-hosted URL.
// Pass `returnUrl` to override where Stripe redirects after success/cancel.
export function createCheckoutSession({ returnUrl, signal, credentials } = {}) {
  return request(`/payments/onboarding/checkout-session/`, {
    method: "POST",
    body: returnUrl ? { return_url: returnUrl } : {},
    signal,
    credentials,
  });
}

// Silent activate — server-side subscription create using saved card.
export function activateWorkspaceBilling(slug, { signal, credentials } = {}) {
  return request(`/payments/workspace/${encodeURIComponent(slug)}/activate/`, {
    method: "POST",
    body: {},
    signal,
    credentials,
  });
}

// Initiate close — sets CLOSING + scheduled_close_at, schedules Stripe cancel.
export function closeWorkspaceBilling(slug, { immediate = false, signal, credentials } = {}) {
  return request(`/payments/workspace/${encodeURIComponent(slug)}/close/`, {
    method: "POST",
    body: { immediate: !!immediate },
    signal,
    credentials,
  });
}

// Lightweight per-workspace lifecycle metadata for WorkspaceLockOverlay /
// banner / badges. Cheap enough to call on every write-heavy page mount.
export function getWorkspaceLifecycle(slug, { signal, credentials } = {}) {
  return request(`/payments/workspace/${encodeURIComponent(slug)}/lifecycle/`, {
    signal,
    credentials,
  });
}

/* ============================== Tenant invitations ============================== */

// Send a teammate invite (broker firm admin → new colleague). Body:
//   { email, role: "admin"|"full_access"|"member", company_id? }
// company_id is optional; backend falls back to the requester's only admin
// firm if not provided.
export function inviteTeammate(body, { signal, credentials } = {}) {
  return request(`/users/invitations/`, {
    method: "POST",
    body,
    signal,
    credentials,
  });
}

/* ============================== Risk profile (workspace) ============================== */

export function saveRiskProfile(orgnr, payload = {}, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/risk-profile/manual/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

export function revertRiskProfile(orgnr, { signal, credentials } = {}) {
  const q = normalizeOrgnr(orgnr);
  return request(`/processing/workspace/${encodeURIComponent(q)}/risk-profile/revert/`, {
    method: "POST",
    signal,
    credentials,
  });
}

// Compatibility shim (no GET endpoint for risk profile)
export async function getRiskProfile(orgnr, { signal, credentials } = {}) {
  const dash = await getWorkspaceDashboard(orgnr, { signal, credentials });
  return dash?.risk_profile || null;
}

/* ============================== Legacy / other apps (unchanged) ============================== */

export function getOrgNumber(companyName, { signal, credentials } = {}) {
  const qs = new URLSearchParams({ name: (companyName || "").trim() });
  return request(`/api/org-number/?${qs.toString()}`, { signal, credentials });
}

export function checkWebsite(url, { signal, credentials } = {}) {
  const qs = new URLSearchParams({ url: (url || "").trim() });
  return request(`/api/check-website/?${qs.toString()}`, { signal, credentials });
}

export function processCompany(slug, payload = {}, { signal, credentials } = {}) {
  return request(`/api/process/${encodeURIComponent(slug)}/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

export function getCompanyFull(slug, { signal, credentials } = {}) {
  return request(`/api/company/${encodeURIComponent(slug)}/full/`, { signal, credentials });
}

/* ============================== IM Studio (unchanged) ============================== */

export function getIMFull(orgnrOrSlug, { signal, credentials } = {}) {
  return request(`/im-studio/company/${encodeURIComponent(orgnrOrSlug)}/full/`, {
    signal,
    credentials,
  });
}

export function saveIMSections(orgnrOrSlug, payload = {}, { signal, credentials } = {}) {
  return request(`/im-studio/company/${encodeURIComponent(orgnrOrSlug)}/sections/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

export function listIMTemplates(slug, { signal, credentials } = {}) {
  return request(`/im-studio/company/${encodeURIComponent(slug)}/templates/`, {
    signal,
    credentials,
  });
}

export function saveIMTemplate(slug, payload = {}, { signal, credentials } = {}) {
  return request(`/im-studio/company/${encodeURIComponent(slug)}/templates/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

export function saveGlobalIMTemplate(payload = {}, { signal, credentials } = {}) {
  return request(`/im-studio/global-templates/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

/* ============================== Pipeline & PM docs (unchanged) ============================== */

export function runPipeline(slug, body = {}, { signal, credentials } = {}) {
  return request(`/api/pipeline/run/${encodeURIComponent(slug)}/`, {
    method: "POST",
    body,
    signal,
    credentials,
  });
}

export function savePMDocument(slug, { title, html, data }, { signal, credentials } = {}) {
  return request(`/api/pm/${encodeURIComponent(slug)}/save/`, {
    method: "POST",
    body: { title, html, data },
    signal,
    credentials,
  });
}

/* ============================== AI agents (unchanged) ============================== */

export function startAgentChain(slug, payload = {}, { signal, credentials } = {}) {
  return request(`/api/agents/chain/${encodeURIComponent(slug)}/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

export function getAgentStatus(slug, { signal, credentials } = {}) {
  return request(`/api/agents/status/${encodeURIComponent(slug)}/`, { signal, credentials });
}

export function summarizeCompany(slug, payload = {}, { signal, credentials } = {}) {
  return request(`/api/agents/summarize/${encodeURIComponent(slug)}/`, {
    method: "POST",
    body: payload,
    signal,
    credentials,
  });
}

/* ============================== OLD SIE upload (DEPRECATED) ============================== */
/**
 * Old pipeline SIE upload:
 *   POST /api/sie/<companySlug>/upload/
 * Keep only if something still uses it.
 * We still route through request() to keep CSRF handling consistent.
 */
export async function uploadSieFiles(companySlug, files = [], { signal, credentials } = {}) {
  if (!files.length) return { uploaded: 0 };
  const form = new FormData();
  files.forEach((f) => form.append("files", f));

  return request(`/api/sie/${encodeURIComponent(companySlug)}/upload/`, {
    method: "POST",
    body: form,
    formData: true,
    signal,
    credentials,
  });
}

/* ============================== Workspace tools (activity / tasks / notes) ============================== */

export function getWorkspaceActivityFeed(workspaceKey, { limit = 50, since } = {}) {
  const key = normalizeOrgnr(workspaceKey);
  const qs = new URLSearchParams();
  if (limit) qs.set("limit", String(limit));
  if (since) qs.set("since", since);
  const tail = qs.toString() ? `?${qs}` : "";
  return request(`/loggs/workspace/${key}/feed/${tail}`);
}

export function listWorkspaceTasks(workspaceKey, { status, assignee } = {}) {
  const key = normalizeOrgnr(workspaceKey);
  const qs = new URLSearchParams();
  if (status) qs.set("status", status);
  if (assignee) qs.set("assignee", assignee);
  const tail = qs.toString() ? `?${qs}` : "";
  return request(`/users/workspaces/${key}/tasks/${tail}`);
}

export function createWorkspaceTask(workspaceKey, payload) {
  const key = normalizeOrgnr(workspaceKey);
  return request(`/users/workspaces/${key}/tasks/`, { method: "POST", body: payload });
}

export function updateWorkspaceTask(workspaceKey, taskId, payload) {
  const key = normalizeOrgnr(workspaceKey);
  return request(`/users/workspaces/${key}/tasks/${taskId}/`, { method: "PATCH", body: payload });
}

export function deleteWorkspaceTask(workspaceKey, taskId) {
  const key = normalizeOrgnr(workspaceKey);
  return request(`/users/workspaces/${key}/tasks/${taskId}/`, { method: "DELETE" });
}

export function listWorkspaceNotes(workspaceKey, { scope, scope_ref_id } = {}) {
  const key = normalizeOrgnr(workspaceKey);
  const qs = new URLSearchParams();
  if (scope) qs.set("scope", scope);
  if (scope_ref_id) qs.set("scope_ref_id", scope_ref_id);
  const tail = qs.toString() ? `?${qs}` : "";
  return request(`/users/workspaces/${key}/notes/${tail}`);
}

export function createWorkspaceNote(workspaceKey, payload) {
  const key = normalizeOrgnr(workspaceKey);
  return request(`/users/workspaces/${key}/notes/`, { method: "POST", body: payload });
}

export function updateWorkspaceNote(workspaceKey, noteId, payload) {
  const key = normalizeOrgnr(workspaceKey);
  return request(`/users/workspaces/${key}/notes/${noteId}/`, { method: "PATCH", body: payload });
}

export function deleteWorkspaceNote(workspaceKey, noteId) {
  const key = normalizeOrgnr(workspaceKey);
  return request(`/users/workspaces/${key}/notes/${noteId}/`, { method: "DELETE" });
}


/* ============================== Default export ============================== */

const serverApi = {
  api,
  request,

  // processing (company)
  validateCompany,
  getCompanyInfo,
  syncCompany,
  listBokslut,
  ensureBokslut,
  getDocumentProcessingStatus,
  uploadFinancials,

  // processing (workspace)
  ensureWorkspace,
  uploadWorkspaceSie,
  getWorkspaceDashboard,
  getWorkspaceAnnualReports,
  saveWorkspaceManualFinancials,

  // questionnaire
  getOwnerQuestionnaire,
  saveOwnerQuestionnaire,

  // valuation
  getCompanyValuation,
  saveCompanyValuationManual,
  revertCompanyValuation,
  useCompanyValuationManual,

  // risk profile
  getRiskProfile,
  saveRiskProfile,
  revertRiskProfile,

  // legacy / other apps
  getOrgNumber,
  checkWebsite,
  processCompany,
  getCompanyFull,

  // IM Studio
  getIMFull,
  saveIMSections,
  listIMTemplates,
  saveIMTemplate,
  saveGlobalIMTemplate,

  // pipeline + PM docs
  runPipeline,
  savePMDocument,

  // agents
  startAgentChain,
  getAgentStatus,
  summarizeCompany,

  // Company-data (served by TIC.io)
  getCompanyEnriched,
  getCompanyFinancials,
  getCompanyFinancialHealth,

  // Buyer matching (FöretagsAPI)
  buyerSearch,

  // Billing (Stripe)
  getBillingStatus,
  createSetupIntent,
  createCheckoutSession,
  activateWorkspaceBilling,
  closeWorkspaceBilling,
  getWorkspaceLifecycle,

  // Tenant invitations
  inviteTeammate,

  // Workspace tools
  getWorkspaceActivityFeed,
  listWorkspaceTasks,
  createWorkspaceTask,
  updateWorkspaceTask,
  deleteWorkspaceTask,
  listWorkspaceNotes,
  createWorkspaceNote,
  updateWorkspaceNote,
  deleteWorkspaceNote,

  // deprecated
  uploadSieFiles,
};

export default serverApi;
